// SF3 — Average Order Value, plus the tiles SF7 will fill.
//
// A KPI tile only, never a series on the trend chart. It reads the same landed
// order lines the chart does, so nothing extra is fetched from Amazon for it.
//
// Average order value moves independently of revenue and that divergence is the
// point: revenue rising while this falls is volume growth at a lower basket
// value, which the revenue line on its own cannot show.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo, FiLock } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../SalesPerformance.module.css';
import {
  Change, ClippedWindow, count, formatChange, formatRange, money,
} from './format';
import { getKpis } from '../services/salesPerformanceApi';

// One account-level growth comparison.
//
// A comparison the history cannot support renders LOCKED with what it is
// waiting for, never as a dash that could be mistaken for flat and never
// hidden: the seller should see the metric exists and will unlock.
const GrowthTile = ({ tile }) => {
  const compared = formatChange(tile.value) !== null;
  const locked = !tile.available;

  return (
    <div className={locked ? `${local.tile} ${local.tileLocked}` : local.tile}>
      <div className={local.tileLabel}>
        {locked && <FiLock className={local.lockIcon} />}
        {tile.label}
      </div>
      <div className={local.tileValue}>
        <Change value={tile.value} />
      </div>
      <div className={local.tileFoot}>
        <span className={local.tileMuted}>
          {compared
            ? `vs ${formatRange(tile.comparison?.start, tile.comparison?.end)}`
            : tile.reason}
        </span>
      </div>
    </div>
  );
};

const KpiStrip = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getKpis(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const aov = data?.average_order_value;
  const compared = formatChange(aov?.change) !== null;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Account performance</h2>
      </div>

      <ClippedWindow data={data} />

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the KPIs'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      <div className={local.tiles}>
        <div className={local.tile}>
          <div className={local.tileLabel}>Average order value</div>
          <div className={local.tileValue}>
            {loading && !data ? '…' : money(aov?.value)}
          </div>
          <div className={local.tileFoot}>
            {compared
              ? (
                <>
                  <Change value={aov.change} />
                  {' against the prior period'}
                </>
              )
              : (
                <span className={local.tileMuted}>
                  {aov?.value === null || aov?.value === undefined
                    ? 'No orders in this period'
                    : 'Not enough history to compare'}
                </span>
              )}
          </div>
          {aov?.orders > 0 && (
            <div className={local.tileFoot}>
              <span className={local.tileMuted}>
                {count(aov.orders)} orders · counted once each, whatever they contain
              </span>
            </div>
          )}
        </div>

        {(data?.growth_indicators || []).map((tile) => (
          <GrowthTile key={tile.unit} tile={tile} />
        ))}
      </div>

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default KpiStrip;
